import { useEffect, useState } from 'react';

import { useShouldShowRideOrderingPortalFeatures } from '../../features/rideOrderingPortal/useShouldShowRideOrderingPortalFeatures';
import { useOrganizationSettingsRetrieveQuery } from '../external/bambi-api/bambiApi';
import { useCanCapturePaymentMethod } from '../forms/PaymentMethodCaptureForm/useCanCapturePaymentMethod';
import { shouldExcludeReportsLink } from './shouldExcludeReportsLink';
import { useRequestedTripBadge } from './useRequestedTripBadge';
import { useUnassignedWillCallTripBadge } from './useUnassignedWillCallTripBadge';
import { nonRideOrderingPortalTopLevelNavItems } from 'src/common/NavBar/nonRideOrderingPortalTopLevelNavItems';
import { rideOrderingPortalTopLevelNavItems } from 'src/common/NavBar/rideOrderingPortalTopLevelNavItems';
import { useUserRoles } from 'src/features/auth/useUserRoles';

export const useNavBarItems = () => {
  const shouldShowRideOrderingNavBar =
    useShouldShowRideOrderingPortalFeatures();

  const { role, isAdmin, isOwner, isBambi, isDispatcher } = useUserRoles();

  const {
    isEnabled: isCapturePaymentsEnabled,
    isLoading,
    onlyInvoicingEnabled,
  } = useCanCapturePaymentMethod();

  const {
    data: organizationSettings,
    isLoading: isOrganizationSettingsLoading,
    isFetching: isOrganizationSettingsFetching,
  } = useOrganizationSettingsRetrieveQuery({});

  const { showRequestedTripBadge, requestedTrips } = useRequestedTripBadge();
  const { unassignedWillCallTrips } = useUnassignedWillCallTripBadge();

  const [navItems, setNavItems] = useState(
    nonRideOrderingPortalTopLevelNavItems
  );

  useEffect(() => {
    const updatedNavItems = [...nonRideOrderingPortalTopLevelNavItems];

    const tripNavItemIndex = updatedNavItems.findIndex(
      (n) => n.href === '/trips'
    );
    if (showRequestedTripBadge && tripNavItemIndex) {
      updatedNavItems[tripNavItemIndex].badgeCount = requestedTrips?.count;
    }

    const dispatchNavItemIndex = updatedNavItems.findIndex(
      (n) => n.href === '/dispatch'
    );

    updatedNavItems[dispatchNavItemIndex].badgeCount =
      unassignedWillCallTrips?.count;

    setNavItems(updatedNavItems);
  }, [
    isLoading,
    isCapturePaymentsEnabled,
    onlyInvoicingEnabled,
    isOrganizationSettingsLoading,
    organizationSettings,
    isOrganizationSettingsFetching,
    showRequestedTripBadge,
    requestedTrips?.count,
    unassignedWillCallTrips?.count,
  ]);

  const filteredNavItems = navItems.filter((item) => {
    if (role && role === 'restricted support') {
      return !['Dispatch', 'Billing', 'Reports'].includes(item.name);
    }

    // Exclude reports link
    if (
      shouldExcludeReportsLink(isBambi, isAdmin, isOwner) &&
      item.href === '/reports'
    ) {
      return false;
    }

    // Exclude billing link for dispatchers
    if (isDispatcher && item.href === '/billing') {
      return false;
    }

    return true;
  });

  const navBarItems = shouldShowRideOrderingNavBar
    ? rideOrderingPortalTopLevelNavItems
    : filteredNavItems;

  return { navBarItems };
};
